/*
 * Case URL scheme. A case is addressed by its individual id, scoped under the
 * client (foreign national) it belongs to:
 *
 *   /clients/:foreignNationalId/cases/:individualId/workspace   canonical
 *   /cases/:individualId/workspace                              no client link
 *
 * The visa type is plain case data and is never part of the URL. The retired
 * scheme `/individual/:id/:visaType/...` is still parsed so old links resolve.
 *
 * No React or IO here so the route rules stay unit-testable; the hook that
 * reads the params lives in `useCaseRoute.ts`.
 */
import type { ArtifactKind } from "./types";

/** The case home segment: `.../workspace`. */
export const CASE_HOME_SEGMENT = "workspace";

export const CLIENTS_SEGMENT = "clients";
export const CASES_SEGMENT = "cases";
/** Root of the retired visa-keyed scheme. */
export const LEGACY_INDIVIDUAL_SEGMENT = "individual";

/** `/cases/new` is the new-case wizard, never a case id. */
export const NEW_CASE_SEGMENT = "new";
export const NEW_CASE_PATH = `/${CASES_SEGMENT}/${NEW_CASE_SEGMENT}`;

/** Where a case lives. `foreignNationalId` is null for unlinked legacy cases. */
export type CaseRef = {
  individualId: string;
  foreignNationalId?: string | null;
};

/** The case a matter (or anything shaped like one) addresses. */
export const matterCaseRef = (m: {
  individual_id: string;
  individual?: { foreign_national_id?: string | null } | null;
}): CaseRef => ({
  individualId: m.individual_id,
  foreignNationalId: m.individual?.foreign_national_id ?? null
});

const enc = encodeURIComponent;

/** `/clients/:fn/cases/:id` or `/cases/:id`, both without a trailing slash. */
export const caseRootPath = (ref: CaseRef): string => {
  const fn = (ref.foreignNationalId ?? "").trim();
  const id = enc(ref.individualId);
  return fn
    ? `/${CLIENTS_SEGMENT}/${enc(fn)}/${CASES_SEGMENT}/${id}`
    : `/${CASES_SEGMENT}/${id}`;
};

/** A page under the case: `casePath(ref, "edit")`. No segment = the case root. */
export const casePath = (ref: CaseRef, segment?: string | null): string => {
  const root = caseRootPath(ref);
  const tail = (segment ?? "").replace(/^\/+/, "");
  return tail ? `${root}/${tail}` : root;
};

const OPEN_PARAM = "open";
const KINDS = new Set<ArtifactKind>([
  "file",
  "card",
  "draft",
  "packet",
  "form",
  "settings",
  "plan",
  "key_facts",
  "time",
  "company",
  "workflows"
]);

export type OpenRequest = { kind: ArtifactKind; id: string };

export const caseWorkspacePath = (
  ref: CaseRef,
  open?: OpenRequest | null
): string => {
  const base = casePath(ref, CASE_HOME_SEGMENT);
  return open
    ? `${base}?${OPEN_PARAM}=${encodeURIComponent(`${open.kind}:${open.id}`)}`
    : base;
};

/** Read `?open=kind:id` off a search string. */
export const parseOpenParam = (search: string): OpenRequest | null => {
  const raw = new URLSearchParams(search).get(OPEN_PARAM);
  if (!raw) return null;
  const i = raw.indexOf(":");
  if (i <= 0) return null;
  const kind = raw.slice(0, i) as ArtifactKind;
  const id = raw.slice(i + 1);
  return KINDS.has(kind) && id ? { kind, id } : null;
};

export const OPEN_SEARCH_PARAM = OPEN_PARAM;

/** Segments under `/individual/:id/` that are not case routes. */
export const NON_CASE_INDIVIDUAL_SEGMENTS = new Set(["account", "lawfirm"]);

export type ParsedCasePath = {
  ref: CaseRef;
  /** Path segments after the case root, e.g. `["drafts", "d1", "edit"]`. */
  segments: string[];
  /** True when the path used the retired `/individual/:id/:visaType` shape. */
  legacy: boolean;
};

const dec = (s: string): string => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

/**
 * Recognise any case URL (current or legacy) and split it into the case and the
 * page underneath. Returns null for anything that is not a case route,
 * including `/individual/:id/account/...` and `/individual/lawfirm`.
 */
export const parseCasePath = (pathname: string): ParsedCasePath | null => {
  const parts = pathname.split("?")[0].split("/").filter(Boolean).map(dec);
  const [a, b, c, d] = parts;
  if (a === CLIENTS_SEGMENT && b && c === CASES_SEGMENT && d) {
    return {
      ref: { individualId: d, foreignNationalId: b },
      segments: parts.slice(4),
      legacy: false
    };
  }
  if (a === CASES_SEGMENT && b && b !== NEW_CASE_SEGMENT) {
    return {
      ref: { individualId: b, foreignNationalId: null },
      segments: parts.slice(2),
      legacy: false
    };
  }
  if (
    a === LEGACY_INDIVIDUAL_SEGMENT &&
    b &&
    !NON_CASE_INDIVIDUAL_SEGMENTS.has(b)
  ) {
    // `/individual/:id` alone (no visa segment) is also a legacy case root.
    if (c && NON_CASE_INDIVIDUAL_SEGMENTS.has(c)) return null;
    return {
      ref: { individualId: b, foreignNationalId: null },
      segments: c ? parts.slice(3) : [],
      legacy: true
    };
  }
  return null;
};

/** The page segment directly under the case root, or undefined at the root. */
export const casePageSegment = (pathname: string): string | undefined =>
  parseCasePath(pathname)?.segments[0];

export const isWorkspacePath = (pathname: string): boolean =>
  casePageSegment(pathname) === CASE_HOME_SEGMENT;

/**
 * Segments the IDE replaced. The workspace is the only case page left, so this
 * is every page the old case-prep UI ever had; the set is kept explicit so the
 * redirect rules below stay unit-testable and unknown segments are handled by
 * the router's catch-all.
 */
export const RETIRED_CASE_SEGMENTS = new Set([
  "documents",
  "packet",
  "forms",
  "drafts",
  "filing-documents",
  "verify",
  "questionnaires",
  "case-research",
  "analysis",
  "edit"
]);

/**
 * Where a retired case page now lives: deep links to a draft or a form open
 * that artifact, the old case-details editor opens the case settings, and
 * everything else lands on the workspace. Returns null when the segments are
 * not a page the IDE took over.
 */
export const retiredSegmentsToWorkspace = (
  ref: CaseRef,
  segments: string[]
): string | null => {
  const [segment, a, b] = segments;
  if (!segment || !RETIRED_CASE_SEGMENTS.has(segment)) return null;
  let open: OpenRequest | null = null;
  if (segment === "drafts" && a && b === "edit") {
    open = { kind: "draft", id: a };
  } else if (segment === "forms" && a) {
    open = { kind: "form", id: a };
  } else if (segment === "edit") {
    open = { kind: "settings", id: ref.individualId };
  }
  return caseWorkspacePath(ref, open);
};

/**
 * Map a legacy case-prep URL (either scheme) to its workspace equivalent, or
 * `null` when the path is not one the IDE took over. `ref` overrides the case
 * parsed from the path, which matters for the old scheme where the client is
 * not in the URL.
 */
export const legacyCasePathToWorkspace = (
  pathname: string,
  ref?: CaseRef
): string | null => {
  const parsed = parseCasePath(pathname);
  if (!parsed) return null;
  return retiredSegmentsToWorkspace(ref ?? parsed.ref, parsed.segments);
};

/**
 * Rebuild a case URL for a different case ref, keeping the page underneath and
 * the search string. Used to canonicalise `/cases/:id/...` once the client is
 * known, and to move `/individual/:id/:visaType/...` links onto the new scheme.
 */
export const rehomeCasePath = (
  pathname: string,
  search: string,
  ref: CaseRef
): string | null => {
  const parsed = parseCasePath(pathname);
  if (!parsed) return null;
  return `${casePath(ref, parsed.segments.map(enc).join("/"))}${search}`;
};
